import { createContext, useCallback, useContext, useMemo } from 'react';
import { useAcceptAIFields, useDismissAIFields } from '../../../../../services/hooks/captable';

export type AIFieldAcceptContextType = {
  acceptField: (securityId: number, fieldName: string, reason?: string) => Promise<void>;
  acceptAllFields: (securityId: number) => Promise<void>;
  dismissField: (securityId: number, fieldName: string, reason?: string) => Promise<void>;
  dismissAllFields: (securityId: number) => Promise<void>;
  /**
   * Remove an AI-CREATED security's column from the grid after the server
   * DELETED the security (its last finding was dismissed - reported as
   * `security_deleted` / `deleted_security_ids`). No API call - grid-only
   * mirror of a deletion that already happened server-side.
   */
  dismissAiColumn: (securityId: number) => void;
};

const AIFieldAcceptContext = createContext<AIFieldAcceptContextType>({
  acceptField: async () => {},
  acceptAllFields: async () => {},
  dismissField: async () => {},
  dismissAllFields: async () => {},
  dismissAiColumn: () => {},
});

export const useAIFieldAcceptContext = () => useContext(AIFieldAcceptContext);

interface AIFieldAcceptProviderProps {
  children: React.ReactNode;
  onDismissAiColumn?: (securityId: number) => void;
}

export const AIFieldAcceptProvider = ({ children, onDismissAiColumn }: AIFieldAcceptProviderProps) => {
  const { acceptAIFields } = useAcceptAIFields();
  const { dismissAIFields } = useDismissAIFields();

  const acceptField = useCallback(
    // `reason` is the optional reviewer note from a single review card; included
    // only when truthy so the default one-click accept keeps a minimal payload.
    async (securityId: number, fieldName: string, reason?: string) => {
      await acceptAIFields(securityId, { field_names: [fieldName], ...(reason ? { reason } : {}) });
    },
    [acceptAIFields]
  );

  const acceptAllFields = useCallback(
    async (securityId: number) => {
      await acceptAIFields(securityId, { all: true });
    },
    [acceptAIFields]
  );

  const dismissField = useCallback(
    // Column removal is SERVER-driven: the backend deletes an AI-CREATED security
    // only when its LAST finding is dismissed and reports it as security_deleted.
    // Declining the name alone no longer removes the column while sibling
    // findings are unsettled, and matched pre-existing securities never delete.
    async (securityId: number, fieldName: string, reason?: string) => {
      const { securityDeleted } = await dismissAIFields(securityId, {
        field_names: [fieldName],
        ...(reason ? { reason } : {}),
      });
      if (securityDeleted) {
        onDismissAiColumn?.(securityId);
      }
    },
    [dismissAIFields, onDismissAiColumn]
  );

  const dismissAllFields = useCallback(
    async (securityId: number) => {
      const { securityDeleted } = await dismissAIFields(securityId, { all: true });
      if (securityDeleted) {
        onDismissAiColumn?.(securityId);
      }
    },
    [dismissAIFields, onDismissAiColumn]
  );

  const dismissAiColumn = useCallback((securityId: number) => onDismissAiColumn?.(securityId), [onDismissAiColumn]);

  const aiFieldAcceptContextValue: AIFieldAcceptContextType = useMemo(
    () => ({ acceptField, acceptAllFields, dismissField, dismissAllFields, dismissAiColumn }),
    [acceptField, acceptAllFields, dismissField, dismissAllFields, dismissAiColumn]
  );

  return <AIFieldAcceptContext.Provider value={aiFieldAcceptContextValue}>{children}</AIFieldAcceptContext.Provider>;
};

export default AIFieldAcceptContext;
